// src/pages/DebtStudio/lib/buildWrapBase.js

import dayjs from "lib/dayjs";
import {
  buildIssueCouponScheduleFromDeal,
  couponToAnnualTotals,
} from "pages/DebtStudio/lib/buildPortfolioDebtService";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function round2(x) {
  const n = num(x, 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function maxDate(rows) {
  let m = null;
  for (const r of rows || []) {
    const d = dayjs(r.date);
    if (!m || d.isAfter(m)) m = d;
  }
  return m;
}

/* ------------------------------------------------------------------
 * Normalizers
 * ------------------------------------------------------------------ */

/**
 * Normalize arbitrary cashflow rows into:
 *   [{ date: ISO, amount: number }]
 *
 * Invalid rows are silently dropped.
 */
export function normalizeWrapCashflows(rows = []) {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((r) => {
      const d = r?.date ? dayjs(r.date) : null;
      if (!d || !d.isValid()) return null;

      const amt =
        num(r.amount) ||
        num(r.total) ||
        num(r.debt_service) ||
        num(r.value);

      if (!Number.isFinite(amt) || amt === 0) return null;

      return {
        date: d.format("YYYY-MM-DD"),
        amount: round2(amt),
      };
    })
    .filter(Boolean);
}

/**
 * Convert a ds_deals row into total-only cashflow rows:
 *   [{ date, amount }]
 *
 * Uses buildIssueCouponScheduleFromDeal.
 */
export function buildWrapCashflowsFromDeal(dealRow) {
  if (!dealRow) return [];

  const couponSchedule = buildIssueCouponScheduleFromDeal(dealRow);
  if (!couponSchedule?.length) return [];

  return couponSchedule.map((r) => ({
    date: r.date,
    amount: round2(num(r.total)),
  }));
}

/**
 * Convert an array of deals into merged total-only cashflows.
 */
export function buildWrapCashflowsFromDeals(deals = []) {
  const out = [];

  for (const deal of deals || []) {
    const rows = buildWrapCashflowsFromDeal(deal);
    for (const r of rows) out.push(r);
  }

  return out;
}

/* ------------------------------------------------------------------
 * Core Builder
 * ------------------------------------------------------------------ */

/**
 * Build Wrap base schedule aggregated by bond year.
 *
 * Parameters:
 *   - datedDate: ISO string (new issue dated date)
 *   - bondYearEnd: ISO string (month/day anchor, e.g. "2026-10-01")
 *   - cashflows: [{ date, amount }]
 *
 * Behavior:
 *   - Uses anchored annual buckets
 *   - Period inclusion rule:
 *       d > period.start && d <= period.end
 *   - Drops periods prior to the one containing datedDate
 *
 * Returns:
 * {
 *   baseByBondYear: [{ date: periodEndISO, total }],
 *   diagnostics: {
 *     empty: boolean,
 *     lastCashflowDate: ISO | null
 *   }
 * }
 */
export function buildWrapBaseByBondYear({
  datedDate,
  bondYearEnd,
  cashflows = [],
}) {
  if (!datedDate || !bondYearEnd) {
    return {
      baseByBondYear: [],
      diagnostics: {
        empty: true,
        lastCashflowDate: null,
      },
    };
  }

  const normalized = normalizeWrapCashflows(cashflows);
  if (!normalized.length) {
    return {
      baseByBondYear: [],
      diagnostics: {
        empty: true,
        lastCashflowDate: null,
      },
    };
  }

  const last = maxDate(normalized);
  if (!last) {
    return {
      baseByBondYear: [],
      diagnostics: {
        empty: true,
        lastCashflowDate: null,
      },
    };
  }

  const totals = couponToAnnualTotals({
    cashflows: normalized,
    startDate: datedDate,
    periodEndAnchor: bondYearEnd,
    startAtDate: datedDate,
  });

  return {
    baseByBondYear: totals,
    diagnostics: {
      empty: totals.length === 0,
      lastCashflowDate: last.format("YYYY-MM-DD"),
    },
  };
}

/**
 * Build Wrap base schedule aggregated to explicit new-issue principal periods.
 *
 * Period inclusion rule:
 *   first period: d <= first principal end
 *   later periods: d > prior principal end && d <= current principal end
 *
 * Returns:
 * {
 *   baseByBondYear: [{ date: principalEndISO, total }],
 *   diagnostics: {
 *     empty: boolean,
 *     lastCashflowDate: ISO | null
 *   }
 * }
 */
export function buildWrapBaseByPrincipalPeriods({
  principalEndDates = [],
  cashflows = [],
}) {
  const ends = (Array.isArray(principalEndDates) ? principalEndDates : [])
    .map((d) => (d ? dayjs(d).startOf("day") : null))
    .filter((d) => d && d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  const normalized = normalizeWrapCashflows(cashflows);
  const last = maxDate(normalized);

  if (!ends.length || !normalized.length) {
    return {
      baseByBondYear: [],
      diagnostics: {
        empty: true,
        lastCashflowDate: last ? last.format("YYYY-MM-DD") : null,
      },
    };
  }

  const inferFirstStepMonths = () => {
    if (ends.length >= 2) {
      const months = ends[1].diff(ends[0], "month");
      if (Number.isFinite(months) && months > 0) return months;
    }

    return 12;
  };

  const firstSyntheticStart = ends[0].subtract(inferFirstStepMonths(), "month");

  const baseByBondYear = ends.map((end, i) => {
    const start = i === 0 ? firstSyntheticStart : ends[i - 1];

    const total = normalized.reduce((sum, cf) => {
      const d = dayjs(cf.date).startOf("day");
      if (!d.isValid()) return sum;

      const afterStart = d.isAfter(start);
      const onOrBeforeEnd = d.isSame(end) || d.isBefore(end);

      return afterStart && onOrBeforeEnd ? sum + num(cf.amount, 0) : sum;
    }, 0);

    return {
      date: end.format("YYYY-MM-DD"),
      total: round2(total),
    };
  });

  return {
    baseByBondYear,
    diagnostics: {
      empty: baseByBondYear.every((r) => num(r.total, 0) === 0),
      lastCashflowDate: last ? last.format("YYYY-MM-DD") : null,
    },
  };
}